import { api } from './client'

/**
 * TASK-0105: mirrors `SearchResultItem` in `SearchController.cs` exactly — camelCase field names
 * (`AddControllers()` in `Program.cs` uses System.Text.Json's default camelCase policy, same
 * convention every other `api/*.ts` file in this frontend already relies on). `type` is always one
 * of the literal strings the backend record's XML doc names, never anything else, so it's typed as
 * a union rather than a bare `string`.
 *
 * TASK-0168 adds `"document"` (backend `SearchDocumentsAsync`) plus the optional
 * `documentEntityType`. `"supplypoint"` (TASK-0147) is deliberately NOT added here: that task's
 * `allowed_paths` did not cover this file, so `GlobalSearch.tsx` widens the type locally for it
 * instead — see the `ResultType` alias there. Untangling that is a separate cleanup, not this
 * task's business.
 *
 * REQ-243-001 adds `"company"` (backend `SearchCompaniesAsync`), `"invoice"`
 * (`SearchInvoicesAsync`) and `"batch"` (`SearchBatchesAsync`) — this file IS inside that
 * requirement's allowed files, unlike `"supplypoint"`'s task, so all three go straight into this
 * shared union rather than a local widening in `GlobalSearch.tsx`.
 *
 * REQ-243-001 review-fix adds `"movement"` (backend `SearchMovementsAsync`) — the requirement's
 * "Movimento" was dropped from the first submission with no acknowledgment even though
 * `CommissionMovement` is a real, already-registered entity (unlike ICCID/Numero linea, which
 * genuinely have no backing column); see the backend controller's class remarks for the full
 * history.
 */
export interface SearchResultItem {
  type: 'customer' | 'lead' | 'contract' | 'document' | 'company' | 'invoice' | 'batch' | 'movement'
  id: number
  label: string
  /**
   * TASK-0168: present only for `type === 'document'`, carrying the owning entity's type
   * (`"Contract"`/`"Customer"`/`"Lead"`/`"Company"`) — `null`/absent for every other type. A
   * document result's `id` is the OWNING entity's id (`Document.EntityId`), not the document's own
   * id, because there is no `/documents/:id` route: `DocumentsPage.tsx` is filtered by the
   * `?entityType=…&entityId=…` pair, so both halves are needed to navigate. See the backend
   * record's XML doc.
   */
  documentEntityType?: string | null
}

/** Mirrors the anonymous `{ query, results }` object `SearchController.Get` returns. */
export interface SearchResponse {
  query: string
  results: SearchResultItem[]
}

export const searchApi = {
  /**
   * `GET /api/search?q=...`. The backend itself already treats a query shorter than its own
   * 2-character minimum as "200 OK, empty results" rather than an error (see the controller's
   * class remarks) — callers on the frontend side should still avoid firing this request at all
   * for such a query (see `GlobalSearch.tsx`), since there is no reason to pay a round trip for a
   * response the backend has already told us will be empty.
   */
  search: (q: string) =>
    api<SearchResponse>(`/api/search?q=${encodeURIComponent(q)}`),
}
